import React, { useEffect, useState, useRef } from 'react';
import { Heart } from 'lucide-react';
import { authClient } from '../../../lib/authClient';
import { checkFavorite, addFavorite, removeFavorite } from '../../../services/favorites';

/**
 * FavoriteButton
 *
 * Self-contained heart icon button that loads the user's saved state from the
 * API on mount, then toggles it on click. Unauthenticated users see a brief
 * "Sign in to save favorites" tooltip instead of triggering an API call.
 *
 * Props:
 *   listingId  {string}  – UUID of the listing to save/unsave.
 *   size       {number}  – Icon size in px (default 16).
 *   className  {string}  – Extra class names applied to the <button> element.
 *   style      {object}  – Inline styles applied to the <button> element.
 */
function FavoriteButton({ listingId, size = 16, className = '', style, id }) {
  const { data: session } = authClient.useSession();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState(false);
  const hintTimerRef = useRef(null);

  // Load saved state once we have a session and a listingId.
  useEffect(() => {
    if (!session?.user || !listingId) return;

    let cancelled = false;

    checkFavorite(listingId)
      .then((data) => {
        if (!cancelled) setIsFavorite(!!data.isFavorite);
      })
      .catch(() => {
        // Ignore errors — default to unfavorited.
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, listingId]);

  // Clear the hint timer on unmount.
  useEffect(() => {
    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, []);

  async function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!session?.user) {
      // Show tooltip hint for unauthenticated users.
      setHint(true);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      hintTimerRef.current = setTimeout(() => setHint(false), 2500);
      return;
    }

    if (loading) return;

    // Optimistic update.
    const next = !isFavorite;
    setIsFavorite(next);
    setLoading(true);

    try {
      if (next) {
        await addFavorite(listingId);
      } else {
        await removeFavorite(listingId);
      }
    } catch {
      // Revert on error.
      setIsFavorite(!next);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div id={id} className="relative inline-flex">
      <button
        type="button"
        className={className}
        style={style}
        aria-label={isFavorite ? 'Remove from saved' : 'Save listing'}
        aria-pressed={isFavorite}
        onClick={handleClick}
        disabled={loading}
      >
        <Heart
          size={size}
          fill={isFavorite ? 'currentColor' : 'none'}
          style={{ transition: 'fill 0.15s ease' }}
        />
      </button>

      {hint && (
        <div
          className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs shadow-lg"
          style={{
            backgroundColor: 'var(--surface, #fff)',
            color: 'var(--page-body-color, #333)',
            border: '1px solid var(--ui-input-border, #d1d5db)',
            zIndex: 50,
          }}
          role="tooltip"
        >
          Sign in to save favorites
        </div>
      )}
    </div>
  );
}

export default FavoriteButton;
